import { applyDarkModeFilter } from "@excalidraw/common";

import type { ElementBackgroundGradient, ElementGradientStop } from "./types";

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Endpoints (element-local coords) of a linear gradient across a w×h box.
 * angle 0 = left→right, 90 = top→bottom.
 */
export const getLinearGradientEndpoints = (
  width: number,
  height: number,
  angle: number,
) => {
  const a = (angle * Math.PI) / 180;
  const cx = width / 2;
  const cy = height / 2;
  const dx = Math.cos(a);
  const dy = Math.sin(a);
  // project the box half-extents onto the direction so the line spans the box
  const halfLen = (Math.abs(width * dx) + Math.abs(height * dy)) / 2;
  return {
    x1: cx - dx * halfLen,
    y1: cy - dy * halfLen,
    x2: cx + dx * halfLen,
    y2: cy + dy * halfLen,
  };
};

/** Center + radius (element-local coords) of a radial gradient over a w×h box. */
export const getRadialGradientGeometry = (width: number, height: number) => ({
  cx: width / 2,
  cy: height / 2,
  r: Math.max(width, height) / 2 || 0.0001,
});

/** CSS gradient string for a preview swatch (always laid out left→right). */
export const gradientToCss = (gradient: ElementBackgroundGradient): string => {
  const stops = [...gradient.stops]
    .sort((a, b) => a.offset - b.offset)
    .map((s) => `${s.color} ${Math.round(clamp01(s.offset) * 100)}%`)
    .join(", ");
  return gradient.type === "radial"
    ? `radial-gradient(circle at center, ${stops})`
    : `linear-gradient(${gradient.angle + 90}deg, ${stops})`;
};

/** Build a CanvasGradient for an element's background gradient. */
export const buildCanvasGradient = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  gradient: ElementBackgroundGradient,
  isDark: boolean,
): CanvasGradient => {
  let grad: CanvasGradient;
  if (gradient.type === "radial") {
    const { cx, cy, r } = getRadialGradientGeometry(width, height);
    grad = context.createRadialGradient(cx, cy, 0, cx, cy, r);
  } else {
    const { x1, y1, x2, y2 } = getLinearGradientEndpoints(
      width,
      height,
      gradient.angle,
    );
    grad = context.createLinearGradient(x1, y1, x2, y2);
  }
  for (const stop of [...gradient.stops].sort((a, b) => a.offset - b.offset)) {
    grad.addColorStop(
      clamp01(stop.offset),
      applyDarkModeFilter(stop.color, isDark),
    );
  }
  return grad;
};

export type GradientDelta = {
  angleDelta?: number;
  offsetShift?: number;
  stopColors?: readonly string[];
} | null;

/** Combines a static gradient with an animation-driven delta (angle
 * rotation, marching stop offsets, per-stop color override) into the
 * gradient actually drawn this frame. Pure — never mutates `gradient`. */
export const applyGradientDelta = <
  G extends {
    type: "linear" | "radial";
    angle: number;
    stops: readonly ElementGradientStop[];
  },
>(
  gradient: G,
  delta: GradientDelta,
): G => {
  if (!delta) {
    return gradient;
  }
  const shift = delta.offsetShift ?? 0;
  return {
    ...gradient,
    angle: gradient.angle + (delta.angleDelta ?? 0),
    stops: gradient.stops.map((stop, i) => ({
      // only wrap when a march is actually in play — wrapping unconditionally
      // maps the offset-1 stop onto 0 and collapses the gradient
      offset: shift ? (((stop.offset + shift) % 1) + 1) % 1 : stop.offset,
      color: delta.stopColors?.[i] ?? stop.color,
    })),
  };
};

/** A sensible default 2-stop gradient seeded from the current solid color. */
export const defaultGradient = (
  type: ElementBackgroundGradient["type"],
  baseColor: string,
): ElementBackgroundGradient => {
  const stops: ElementGradientStop[] = [
    { offset: 0, color: "#ffffff" },
    {
      offset: 1,
      color: !baseColor || baseColor === "transparent" ? "#4dabf7" : baseColor,
    },
  ];
  return { type, angle: 90, stops };
};
